import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import { useState } from "react"
import type { CombinedError } from "urql"

import { useInventoryAdjustmentHistoryColumns } from "../hooks/useInventoryAdjustmentHistoryColumns"

import { InventoryAdjustmentHistoryExpandedRow } from "./InventoryAdjustmentHistoryRow"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { InventoryAdjustmentType } from "@/graphql/codegen/graphql"

interface InventoryAdjustmentHistoryTableProps {
  data: AdjustmentHistoryItem[]
  loading?: boolean
  error?: CombinedError | Error
  sorting: SortingState
  containerRef?: RefObject<HTMLDivElement>
  onSortingChange: OnChangeFn<SortingState>
  onScroll?: () => void
  onDocumentsOpen: (item: AdjustmentHistoryItem) => void
}

export const InventoryAdjustmentHistoryTable: FC<InventoryAdjustmentHistoryTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  containerRef,
  onSortingChange,
  onScroll,
  onDocumentsOpen,
}) => {
  const [expandedState, setExpandedState] = useState<ExpandedState>({})

  const columns = useInventoryAdjustmentHistoryColumns({ onDocumentsOpen })

  return (
    <AerosBaseTable
      error={error}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
            expanded: expandedState,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          onSortingChange,
          onExpandedChange: setExpandedState,
          getRowCanExpand: (row) =>
            row.original.adjustmentType === InventoryAdjustmentType.AdjustQuantity ||
            row.original.adjustmentType === InventoryAdjustmentType.As400Adjustment,
          renderExpandedContent: (row) => <InventoryAdjustmentHistoryExpandedRow row={row} />,
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyCell: { className: "align-top" },
      }}
      noDataMessage='No results found according to your search criteria'
    />
  )
}
